<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, FormControl, Input, Modal, Option, Select, type SelectOption } from '@ui'
import type { MetricTarget } from '@/types/intelligence'
import { formatValue, metricName } from '@/lib/action-text'
import type { ObjectiveMeasure } from '@/lib/objective-measures'
import { addDays } from '@/lib/opportunity-text'
import DayPicker from '../DayPicker.vue'

export interface ObjectivePayload {
  metricId: string
  title: string
  targetValue: number
  targetDate: string
}

/**
 * Set objective (S0 §37, P5 §2): a target on one of the Overview's five
 * metrics, or on a care journey share (DECISIONS.md #45), by a date. Its
 * baseline is the last 30 days as the Overview measures them, or the share as
 * it stands today, shown here and never typed (Dao, 1 Oct 2026). A measure
 * with no value there cannot take one. Editing keeps the measure and baseline
 * and moves the title, target or date.
 */
const props = withDefaults(
  defineProps<{
    open: boolean
    /** The five metrics over the last 30 days and the care shares as they stand: where each one's baseline comes from. Null while loading. */
    measures: ObjectiveMeasure[] | null
    /** Today in the workspace's time zone: a target date is after it. */
    today: string
    /** Set from an opportunity: the objective is on its metric. */
    fixedMetricId?: string
    /** Editing this one. */
    objective?: MetricTarget
    pending?: boolean
  }>(),
  { fixedMetricId: undefined, objective: undefined, pending: false },
)
const emit = defineEmits<{ close: []; submit: [objective: ObjectivePayload] }>()
const { t, locale } = useI18n()

const metricId = ref<string | null>(null)
const title = ref('')
/**
 * The kit's Input in a number field (it is themed for dark mode, its
 * NumberInput is not): it hands back a number once one is typed, and text
 * while the field is empty or being edited.
 */
const targetText = ref<string | number>('')
/** Typed as text, but a number field's Input sends a number. */
const setTarget = (value: unknown): void => {
  targetText.value = typeof value === 'number' ? value : String(value ?? '')
}
const targetValue = computed<number | null>(() => {
  const text = String(targetText.value ?? '').trim().replace(',', '.')
  const value = text === '' ? NaN : Number(text)
  return Number.isFinite(value) && value >= 0 ? value : null
})
const targetDate = ref<string | null>(null)
const titleTouched = ref(false)

const editing = computed(() => props.objective !== undefined)

watch(
  () => props.open,
  (open) => {
    if (!open) return
    const objective = props.objective
    metricId.value = objective?.metricId ?? props.fixedMetricId ?? null
    // Named after its metric until the reader names it.
    title.value = objective?.title ?? (metricId.value ? metricName(t, metricId.value) : '')
    targetText.value = objective ? String(objective.targetValue) : ''
    targetDate.value = objective?.targetDate ?? null
    titleTouched.value = editing.value
  },
  { immediate: true },
)

const options = computed<SelectOption[]>(() =>
  (props.measures ?? []).map((measure) => ({ value: measure.metricId, label: metricName(t, measure.metricId) })),
)
const chosen = computed(() => options.value.find((option) => option.value === metricId.value))
const choose = (option: SelectOption | SelectOption[] | undefined): void => {
  if (!option || Array.isArray(option)) return
  metricId.value = String(option.value)
  if (!titleTouched.value) title.value = metricName(t, metricId.value)
}

const metric = computed(() => props.measures?.find((candidate) => candidate.metricId === metricId.value) ?? null)
const unit = computed(() => props.objective?.unit ?? metric.value?.unit ?? 'COUNT')
const betterWhen = computed(() => props.objective?.betterWhen ?? metric.value?.betterWhen ?? null)
const baseline = computed<number | null>(() => props.objective?.baselineValue ?? metric.value?.value ?? null)

const baselineText = computed(() => {
  if (props.objective) {
    return t('objectives.form.baselineSet', { value: formatValue(t, props.objective.baselineValue, unit.value, locale.value) })
  }
  if (!metricId.value) return ''
  if (!metric.value) return t('common.loading')
  const share = unit.value === 'PERCENT'
  if (baseline.value === null) return t(share ? 'objectives.form.notMeasurableNow' : 'objectives.form.notMeasurable')
  return t(share ? 'objectives.form.baselineNow' : 'objectives.form.baseline', { value: formatValue(t, baseline.value, unit.value, locale.value) })
})

/** The target lies away from the baseline, and a waiting time can only be set to fall (S0 §37.2). */
const targetProblem = computed(() => {
  if (targetValue.value === null || baseline.value === null) return null
  if (targetValue.value === baseline.value) return t('objectives.form.targetIsBaseline')
  if (betterWhen.value === 'LOWER' && targetValue.value > baseline.value) return t('objectives.form.targetMustFall')
  if (betterWhen.value === 'HIGHER' && targetValue.value < baseline.value) return t('objectives.form.targetMustRise')
  if (unit.value === 'PERCENT' && targetValue.value > 100) return t('objectives.form.targetAtMostHundred')
  return null
})

const firstDay = computed(() => addDays(props.today, 1))

const canSubmit = computed(
  () =>
    metricId.value !== null &&
    baseline.value !== null &&
    title.value.trim() !== '' &&
    targetValue.value !== null &&
    targetProblem.value === null &&
    targetDate.value !== null &&
    targetDate.value > props.today,
)

const submit = (): void => {
  if (!canSubmit.value || !metricId.value || targetValue.value === null || !targetDate.value) return
  emit('submit', { metricId: metricId.value, title: title.value.trim(), targetValue: targetValue.value, targetDate: targetDate.value })
}
</script>

<template>
  <Modal :model-value="open" :title="t(editing ? 'objectives.form.editTitle' : 'objectives.form.title')" size="md" type="content" no-icon @close="emit('close')">
    <template #content>
      <div class="form" data-test="objective-form">
        <FormControl name="objective-metric" :label="t('objectives.form.metric')" required>
          <Select
            :model-value="chosen"
            :options="options"
            :disabled="editing || fixedMetricId !== undefined"
            :placeholder="t('objectives.form.chooseMetric')"
            data-test="objective-metric"
            @update:model-value="choose"
          >
            <Option v-for="option in options" :key="option.value" :label="option.label" :value="option.value" />
          </Select>
        </FormControl>
        <p v-if="baselineText" class="form__baseline" data-test="objective-baseline">{{ baselineText }}</p>
        <FormControl name="objective-title" :label="t('objectives.form.name')" required>
          <Input v-model="title" :maxlength="140" data-test="objective-title" @update:model-value="titleTouched = true" />
        </FormControl>
        <FormControl
          name="objective-target"
          :label="unit === 'MINUTES' ? t('objectives.form.targetMinutes') : unit === 'PERCENT' ? t('objectives.form.targetPercent') : t('objectives.form.target')"
          :error-message="targetProblem ?? undefined"
          required
        >
          <Input
            :model-value="String(targetText)"
            type="number"
            min="0"
            :max="unit === 'PERCENT' ? 100 : undefined"
            :step="unit === 'MINUTES' ? 0.5 : 1"
            inputmode="decimal"
            data-test="objective-target"
            @update:model-value="setTarget"
          />
        </FormControl>
        <FormControl name="objective-date" :label="t('objectives.form.targetDate')" :help-text="t('objectives.form.targetDateHelp')" required>
          <DayPicker v-model="targetDate" :min="firstDay" data-test="objective-date" />
        </FormControl>
      </div>
    </template>

    <template #actions>
      <Button type="secondary" @click="emit('close')">{{ t('common.cancel') }}</Button>
      <Button :loading="pending" :disabled="!canSubmit" :aria-disabled="!canSubmit" data-test="objective-submit" @click="submit">
        {{ t(editing ? 'common.save' : 'objectives.form.submit') }}
      </Button>
    </template>
  </Modal>
</template>

<style scoped>
.form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
}

.form__baseline {
  margin: 0;
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--radius-lg);
  background: var(--surface-sunken);
  font-size: var(--font-size-body-3);
  line-height: var(--line-height-body-3);
  color: var(--fg-secondary);
}
</style>
